import * as React from "react";
import { useI18n } from "../../lib/i18n";
import type {
  CommandDangerLevel,
  CommandExecutionSource,
  CommandExecutionStatus,
} from "../../lib/api/commands";
import * as s from "./commands.css";

const STATUS_TONE: Record<CommandExecutionStatus, keyof typeof s.pill> = {
  sent: "info",
  succeeded: "success",
  failed: "danger",
  timed_out: "warning",
  rejected: "danger",
};

export function CommandStatusPill({
  status,
  phase,
}: {
  status: CommandExecutionStatus;
  phase?: "pre_read" | "main";
}) {
  const { t } = useI18n();
  const label =
    status === "sent" && phase === "pre_read"
      ? t("pro.commands.status.preRead")
      : t(`pro.commands.status.${status}`);
  return (
    <span className={s.pill[STATUS_TONE[status]]}>
      {status === "sent" && <span className={s.pulseDot} aria-hidden="true" />}
      {label}
    </span>
  );
}

export function CommandDangerPill({ level }: { level: CommandDangerLevel | null | undefined }) {
  const { t } = useI18n();
  if (!level || level === "normal") return null;
  return (
    <span className={level === "irreversible" ? s.pill.danger : s.pill.warning}>
      {t(`pro.commands.danger.${level}`)}
    </span>
  );
}

export function commandSourceLabel(
  t: (key: string) => string,
  source: CommandExecutionSource,
): string {
  return t(`pro.commands.source.${source}`);
}

export function formatCommandTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function useRelativeElapsed(fromIso: string | null | undefined, active: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  if (!fromIso) return 0;
  return Math.max(0, Math.round((now - new Date(fromIso).getTime()) / 1000));
}
